import {
  presentEnvironmentRecovery,
  type EnvironmentConnectionPresentation,
} from "@afar-research/client-runtime/state/presentation";
import { WifiOffIcon } from "lucide-react";

import { EnvironmentRecoveryButton } from "./EnvironmentRecoveryButton";
import {
  WorkspaceEmptyStateFrame,
  WorkspaceEmptyStateInset,
  WorkspaceEmptyStateMessage,
} from "./WorkspaceEmptyStateFrame";

export interface ThreadUnavailableStateProps {
  readonly label: string;
  readonly connection: EnvironmentConnectionPresentation;
  readonly onRetry: () => void;
  readonly onOpenConnectionSettings: () => void;
}

/**
 * Stands in for a thread the client cannot read because its Environment is unreachable
 * and nothing of the thread was retained. It exists so the no-thread-selected empty state
 * never has to answer for a connection failure, and so recovery stays reachable on a
 * surface with no composer to hang a banner on.
 */
export function ThreadUnavailableStateView(props: ThreadUnavailableStateProps) {
  const recovery = presentEnvironmentRecovery(props.connection);
  return (
    <WorkspaceEmptyStateFrame heading={`${props.label}: ${recovery.statusText}`}>
      <WorkspaceEmptyStateMessage
        icon={<WifiOffIcon className="size-5 text-muted-foreground/60" aria-hidden />}
        title={
          props.connection.endpointStarting
            ? `${props.label} is starting`
            : "This thread is out of reach"
        }
        detail={
          props.connection.endpointStarting
            ? `This thread opens once ${props.label} is ready. ${recovery.recoveryDetail}`
            : `${props.label} is not connected, so this thread cannot be read or continued yet. ${recovery.recoveryDetail}`
        }
        action={
          <EnvironmentRecoveryButton
            connection={props.connection}
            onRetry={props.onRetry}
            onOpenConnectionSettings={props.onOpenConnectionSettings}
          />
        }
      />
    </WorkspaceEmptyStateFrame>
  );
}

export function ThreadUnavailableState(props: ThreadUnavailableStateProps) {
  return (
    <WorkspaceEmptyStateInset>
      <ThreadUnavailableStateView {...props} />
    </WorkspaceEmptyStateInset>
  );
}
